import { Currency } from '@uniswap/sdk-core'

import { SupportedChainId } from './chains'
import {
  AAPLUS_DUCX_MAINNET,
  AAPLUS_DUCX_TESTNET,
  BULLION_DUCX_MAINNET,
  BULLION_DUCX_TESTNET,
  DUC_DUCX_MAINNET,
  DUC_DUCX_TESTNET,
  GOLDMV_DUCX_MAINNET,
  GOLDMV_DUCX_TESTNET,
  SHBT_DUCX_MAINNET,
  SHBT_DUCX_TESTNET,
  SHRES_DUCX_MAINNET,
  SHRES_DUCX_TESTNET,
  XGEN_DUCX_MAINNET,
  XGEN_DUCX_TESTNET,
} from './tokens'

const NATIVE = 'NATIVE' as const

type CurrencyIdentifier = typeof NATIVE | string
type SwapGroupTokens = CurrencyIdentifier[]
type NormalizedPair = [CurrencyIdentifier, CurrencyIdentifier]

/** We have strict rules for swap pairs, according to technical requirements. **/
interface SwapGroupConfig {
  tokens: SwapGroupTokens
  allowedPairs: NormalizedPair[]
}

type ChainSwapGroups = { [chainId: number]: SwapGroupConfig[] }

const addr = (t: { address: string }) => t.address.toLowerCase()

const SWAP_GROUPS: ChainSwapGroups = {
  [SupportedChainId.DUCX_TESTNET]: [
    {
      tokens: [NATIVE, addr(XGEN_DUCX_TESTNET), addr(BULLION_DUCX_TESTNET)],
      allowedPairs: [
        [NATIVE, addr(XGEN_DUCX_TESTNET)],
        [NATIVE, addr(BULLION_DUCX_TESTNET)],
      ],
    },
    {
      tokens: [
        addr(DUC_DUCX_TESTNET),
        addr(GOLDMV_DUCX_TESTNET),
        addr(AAPLUS_DUCX_TESTNET),
        addr(SHBT_DUCX_TESTNET),
        addr(SHRES_DUCX_TESTNET),
      ],
      allowedPairs: [
        [addr(DUC_DUCX_TESTNET), addr(AAPLUS_DUCX_TESTNET)],
        [addr(DUC_DUCX_TESTNET), addr(SHBT_DUCX_TESTNET)],
        [addr(DUC_DUCX_TESTNET), addr(SHRES_DUCX_TESTNET)],
        [addr(GOLDMV_DUCX_TESTNET), addr(AAPLUS_DUCX_TESTNET)],
        [addr(GOLDMV_DUCX_TESTNET), addr(SHBT_DUCX_TESTNET)],
        [addr(GOLDMV_DUCX_TESTNET), addr(SHRES_DUCX_TESTNET)],
      ],
    },
  ],
  [SupportedChainId.DUCX]: [
    {
      tokens: [NATIVE, addr(XGEN_DUCX_MAINNET), addr(BULLION_DUCX_MAINNET)],
      allowedPairs: [
        [NATIVE, addr(XGEN_DUCX_MAINNET)],
        [NATIVE, addr(BULLION_DUCX_MAINNET)],
      ],
    },
    {
      tokens: [
        addr(DUC_DUCX_MAINNET),
        addr(GOLDMV_DUCX_MAINNET),
        addr(AAPLUS_DUCX_MAINNET),
        addr(SHBT_DUCX_MAINNET),
        addr(SHRES_DUCX_MAINNET),
      ],
      allowedPairs: [
        [addr(DUC_DUCX_MAINNET), addr(AAPLUS_DUCX_MAINNET)],
        [addr(DUC_DUCX_MAINNET), addr(SHBT_DUCX_MAINNET)],
        [addr(DUC_DUCX_MAINNET), addr(SHRES_DUCX_MAINNET)],
        [addr(GOLDMV_DUCX_MAINNET), addr(AAPLUS_DUCX_MAINNET)],
        [addr(GOLDMV_DUCX_MAINNET), addr(SHBT_DUCX_MAINNET)],
        [addr(GOLDMV_DUCX_MAINNET), addr(SHRES_DUCX_MAINNET)],
      ],
    },
  ],
}

function getCurrencyId(currency: Currency): CurrencyIdentifier {
  if (currency.isNative) {
    return NATIVE
  }
  return currency.wrapped.address.toLowerCase()
}

function findCurrencyGroup(currencyId: CurrencyIdentifier, groups: SwapGroupConfig[]): SwapGroupConfig | undefined {
  return groups.find((group) => group.tokens.includes(currencyId))
}

function normalizePairId(a: CurrencyIdentifier, b: CurrencyIdentifier): NormalizedPair {
  return a < b ? [a, b] : [b, a]
}

function isPairAllowedInGroup(
  group: SwapGroupConfig | undefined,
  aId: CurrencyIdentifier,
  bId: CurrencyIdentifier
): boolean {
  if (!group || !group.allowedPairs || group.allowedPairs.length === 0) return false

  const pair = normalizePairId(aId, bId)

  return group.allowedPairs.some(([allowedA, allowedB]) => {
    const normalizedAllowed = normalizePairId(allowedA, allowedB)
    return pair[0] === normalizedAllowed[0] && pair[1] === normalizedAllowed[1]
  })
}

/**
 * Check if selecting `currency` is restricted when `otherCurrency` is already selected
 *
 * Rules:
 * - If neither currency is in any group -> allowed (not restricted)
 * - If one is in a group and the other is not -> restricted
 * - If they are in different groups -> restricted
 * - If same group: only allowed if the pair is in that group's allowedPairs (otherwise restricted)
 */
export function isRestrictedSwapPair(
  currency: Currency,
  otherCurrency: Currency | null | undefined,
  chainId?: number
): boolean {
  if (!otherCurrency || !chainId) return false

  const groups = SWAP_GROUPS[chainId]
  if (!groups || groups.length === 0) return false

  const currencyId = getCurrencyId(currency)
  const otherCurrencyId = getCurrencyId(otherCurrency)

  const currencyGroup = findCurrencyGroup(currencyId, groups)
  const otherCurrencyGroup = findCurrencyGroup(otherCurrencyId, groups)

  if (!currencyGroup && !otherCurrencyGroup) return false

  if (!currencyGroup || !otherCurrencyGroup) return true

  if (currencyGroup !== otherCurrencyGroup) return true

  // Same group: only allowed if the pair is in allowedPairs
  return !isPairAllowedInGroup(currencyGroup, currencyId, otherCurrencyId)
}

export function areSwapGroupsCompatible(
  currencyA: Currency | null | undefined,
  currencyB: Currency | null | undefined,
  chainId?: number
): boolean {
  if (!currencyA || !currencyB || !chainId) return true

  return !isRestrictedSwapPair(currencyA, currencyB, chainId)
}
